import type {
  ApiClient,
  ApiRequest,
  AppRuntime,
  ApprovalDecisionValue,
  ApprovalInstance,
  ApprovalStatus,
  ApprovalSubmissionTemplate,
  ApprovalView,
  ActivationInfo,
  ActivatePayload,
  Application,
  ApplicationVersion,
  CatalogApplication,
  CaptchaPublicConfig,
  ChangePasswordPayload,
  ChangeTenantPasswordPayload,
  CreateDeveloperApplicationPayload,
  CreateTenantTicketPayload,
  CreateOAuthClientPayload,
  CreateOAuthClientSecretPayload,
  CreateOAuthClientSecretResponse,
  CreateOAuthRedirectUriPayload,
  DeleteOAuthRedirectUriResponse,
  DeveloperApplication,
  DeveloperApplicationRoute,
  DeveloperApplicationRouteKind,
  DeveloperApplicationScopeGrant,
  ManagedAppConfigEnvironment,
  ManagedAppConfigSnapshot,
  DriveLimitSummary,
  CreateFaaSFunctionAccessTokenPayload,
  CreateFaaSFunctionPayload,
  FaaSDenoVersionSource,
  FaaSBundle,
  FaaSBundleDetail,
  FaaSBundleImportResult,
  FaaSBundleInstallation,
  FaaSBundleInstallationHistory,
  FaaSBundleInstallationRequest,
  FaaSBundleInstallationRequestSummary,
  FaaSBundleInstallationSummary,
  FaaSInstallationConfiguration,
  FaaSBundleMemberDetail,
  FaaSFunction,
  FaaSFunctionAccessToken,
  FaaSGrantableTool,
  FaaSInvocation,
  FaaSOAuthClientToolPolicy,
  FaaSVersion,
  Friend,
  FriendRequest,
  JoinRequest,
  OffsetPage,
  OAuthClient,
  OAuthClientSecret,
  OAuthAuthorizationDecisionPayload,
  OAuthAuthorizationDecisionResponse,
  OAuthAuthorizationDetails,
  OAuthRedirectUri,
  OAuthScope,
  OAuthUserAuthorization,
  NotificationChangedEvent,
  NotificationListOptions,
  NotificationPage,
  NotificationPreference,
  NotificationPushDevice,
  NotificationPushDeviceRegistration,
  NotificationRuntime,
  Page,
  PasswordCodeChallenge,
  PreAuthChallenge,
  PublishFaaSDenoSourcePayload,
  RegisterPayload,
  RevokeOAuthClientSecretResponse,
  ReplaceFaaSOAuthClientToolGrantsPayload,
  ResetPasswordPayload,
  SearchResult,
  Session,
  SkillContextPlan,
  SkillMarketItem,
  SkillDraft,
  SkillDraftSummary,
  SkillDraftReceipt,
  SaveSkillDraftPayload,
  FreezeSkillDraftPayload,
  SkillArchiveInspection,
  SkillPublicationVersion,
  PublishSkillBundlePayload,
  SkillTenantPolicy,
  SkillExposureMode,
  UserSkillPreferences,
  Tenant,
  TenantAuthPolicy,
  TenantBillingOverview,
  TenantKind,
  TenantSummary,
  TenantTicket,
  TenantUsageSummary,
  TokenPair,
  User,
  InvokeFaaSFunctionPayload,
  IssuedFaaSFunctionAccessToken,
  UpdateDeveloperApplicationPayload,
  UpsertDeveloperApplicationRoutePayload,
  StaticSite,
  StaticSiteRelease,
  StaticSiteReleasePage,
  StaticSiteUploadSession,
  SaveManagedAppConfigPayload,
  UpdateDeveloperApplicationScopesPayload,
  ScopeApprovalRequest,
  SubmitScopeApprovalRequestsPayload,
  SubmitApprovalInput,
  UpdateOAuthClientPayload,
  VerificationChallenge,
  VerificationPurpose,
} from "@ark/contracts";

const storageKey = "ark.session.v1";
const sessionChannelName = "ark.session.v1.sync";
const sessionRefreshLock = "ark.session.v1.refresh";
const sessionRestoreSuppressedKey = "ark.session.v1.restore-suppressed";
const sessionCookieIdentityKey = "ark.session.v1.cookie-identity";

interface BrowserCookieIdentity {
  tenantId: string | null;
  userId: string | null;
  version: number;
}

interface SessionUpdateMessage {
  at: number;
  cookieVersion?: number;
  session: Session | null;
  source: string;
  type: "session";
}

interface SessionRequestMessage {
  source: string;
  type: "session-request";
}

type SessionSyncMessage = SessionRequestMessage | SessionUpdateMessage;

export class ApiError extends Error {
  readonly code: number | undefined;
  readonly reason: string | undefined;
  readonly trace: string | undefined;

  constructor(
    message: string,
    readonly status: number,
    readonly detail?: unknown,
    trace?: string,
  ) {
    super(message);
    this.name = "ApiError";
    const meta = envelopeMeta(detail);
    this.code = envelopeCode(detail);
    this.reason = meta?.reason;
    this.trace = trace || meta?.trace;
  }
}

// Envelope 是后端统一响应外壳 {code, message, data, meta}。
interface Envelope {
  code: number;
  message: string;
  data?: unknown;
  meta?: { errCode?: string; reason?: string; trace?: string };
}

interface LoginResult extends TokenPair {
  user: User;
}

// RegisterResult 在 LoginResult 基础上带上个人租户信息，供注册后自动登录组装 session。
interface RegisterResult extends LoginResult {
  tenant: {
    tenantId: string;
    tenantName: string;
    tenantKind?: TenantKind;
  };
}

interface IdentifyResult {
  preAuthToken: string;
  expiresAt: string;
}

interface TenantOptionResult {
  tenantId: string;
  tenantName: string;
  tenantKind?: TenantKind;
  stepUpRequired: boolean;
}

interface TenantsResult {
  items: TenantOptionResult[] | null;
  total: number;
}

interface SessionRestoreResult extends Omit<LoginResult, "refreshToken"> {
  tenant: TenantOptionResult;
  tenants: TenantOptionResult[];
}

type SessionRefreshResult = Omit<TokenPair, "refreshToken"> & {
  refreshToken?: string;
};

type Listener = () => void;

export type GlobalErrorHandler = (
  error: unknown,
  config: { method: string; path: string },
) => void;

type NotificationListener = (event: NotificationChangedEvent) => void;

export class NotificationManager {
  private readonly listeners = new Map<string, Set<NotificationListener>>();
  private source: EventSource | null = null;
  private connecting = false;
  private generation = 0;
  private reconnectAttempt = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private readonly api: ApiClient,
    private readonly getSession: () => Session | null,
  ) {}

  bind(channel?: string): NotificationRuntime {
    const normalized = channel?.trim() || undefined;
    return {
      ...(normalized ? { channel: normalized } : {}),
      list: (options) => this.list(normalized, options),
      listPreferences: () => this.listPreferences(normalized),
      markAllRead: () => this.markAllRead(normalized),
      markRead: (notificationId) => this.markRead(notificationId),
      registerPushDevice: (device) => this.registerPushDevice(device),
      subscribe: (listener) => this.subscribe(normalized, listener),
      unreadCount: () => this.unreadCount(normalized),
      unregisterPushDevice: (deviceId) => this.unregisterPushDevice(deviceId),
      updatePreference: (preference) =>
        this.updatePreference(
          normalized ? { ...preference, channel: normalized } : preference,
        ),
    };
  }

  sessionChanged(): void {
    this.generation++;
    this.closeSource();
    if (this.getSession() && this.listenerCount() > 0) {
      this.ensureConnected();
    }
  }

  private list(
    channel: string | undefined,
    options: NotificationListOptions = {},
  ): Promise<NotificationPage> {
    const query = new URLSearchParams({
      page: String(options.page ?? 1),
      pageSize: String(options.pageSize ?? 20),
    });
    if (channel) query.set("channel", channel);
    if (options.unreadOnly) query.set("unreadOnly", "true");
    return this.api.get<NotificationPage>(
      `/notifications?${query.toString()}`,
      { onError: () => undefined },
    );
  }

  private unreadCount(channel?: string): Promise<number> {
    const query = channel ? `?channel=${encodeURIComponent(channel)}` : "";
    return this.api
      .get<{ count: number }>(`/notifications/unread-count${query}`, {
        onError: () => undefined,
      })
      .then(({ count }) => count);
  }

  private markRead(notificationId: string): Promise<void> {
    return this.api
      .post(`/notifications/${encodeURIComponent(notificationId)}/read`, {
        onError: () => undefined,
      })
      .then(() => undefined);
  }

  private markAllRead(channel?: string): Promise<number> {
    return this.api
      .post<{ updated: number }>("/notifications/read-all", {
        body: { channel },
        onError: () => undefined,
      })
      .then(({ updated }) => updated);
  }

  private listPreferences(channel?: string): Promise<NotificationPreference[]> {
    return this.api
      .get<{ items: NotificationPreference[] }>("/notifications/preferences", {
        onError: () => undefined,
      })
      .then(({ items }) =>
        channel
          ? items.filter(
              (item) => item.channel === channel || item.channel === "*",
            )
          : items,
      );
  }

  private updatePreference(
    preference: Omit<NotificationPreference, "updatedAt">,
  ): Promise<NotificationPreference> {
    return this.api.put<NotificationPreference>("/notifications/preferences", {
      body: preference,
      onError: () => undefined,
    });
  }

  private registerPushDevice(
    device: NotificationPushDeviceRegistration,
  ): Promise<NotificationPushDevice> {
    return this.api.post<NotificationPushDevice>("/notifications/devices", {
      body: device,
      onError: () => undefined,
    });
  }

  private unregisterPushDevice(deviceId: string): Promise<void> {
    return this.api
      .delete(`/notifications/devices/${encodeURIComponent(deviceId)}`, {
        onError: () => undefined,
      })
      .then(() => undefined);
  }

  private subscribe(
    channel: string | undefined,
    listener: NotificationListener,
  ): () => void {
    const key = channel ?? "";
    const current = this.listeners.get(key) ?? new Set<NotificationListener>();
    current.add(listener);
    this.listeners.set(key, current);
    this.ensureConnected();
    return () => {
      current.delete(listener);
      if (current.size === 0) this.listeners.delete(key);
      if (this.listenerCount() === 0) {
        this.generation++;
        this.closeSource();
      }
    };
  }

  private ensureConnected(): void {
    if (
      this.source ||
      this.connecting ||
      !this.getSession() ||
      this.listenerCount() === 0 ||
      typeof EventSource === "undefined"
    ) {
      return;
    }
    this.connecting = true;
    const generation = this.generation;
    void this.api
      .post<{ ticket: string }>("/notifications/stream/ticket", {
        onError: () => undefined,
      })
      .then(({ ticket }) => {
        if (
          generation !== this.generation ||
          !this.getSession() ||
          this.listenerCount() === 0
        ) {
          return;
        }
        const source = new EventSource(
          this.api.resolve(
            `/notifications/stream?ticket=${encodeURIComponent(ticket)}`,
          ),
        );
        this.source = source;
        source.onopen = () => {
          this.reconnectAttempt = 0;
        };
        source.addEventListener("notification", (raw) => {
          try {
            this.dispatch(
              JSON.parse(
                (raw as MessageEvent<string>).data,
              ) as NotificationChangedEvent,
            );
          } catch {
            // Malformed wake-up events are ignored; list APIs remain the truth.
          }
        });
        source.onerror = () => {
          if (this.source === source) {
            this.source = null;
          }
          source.close();
          this.scheduleReconnect();
        };
      })
      .catch(() => {
        if (generation === this.generation) this.scheduleReconnect();
      })
      .finally(() => {
        if (generation === this.generation) this.connecting = false;
      });
  }

  private dispatch(event: NotificationChangedEvent): void {
    this.listeners.get(event.channel)?.forEach((listener) => listener(event));
    if (event.visibility === "global" || event.visibility === "both") {
      this.listeners.get("")?.forEach((listener) => listener(event));
    }
  }

  private scheduleReconnect(): void {
    if (
      this.reconnectTimer ||
      !this.getSession() ||
      this.listenerCount() === 0
    ) {
      return;
    }
    const delay = Math.min(30_000, 500 * 2 ** this.reconnectAttempt++);
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.ensureConnected();
    }, delay);
  }

  private closeSource(): void {
    this.source?.close();
    this.source = null;
    this.connecting = false;
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;
    this.reconnectAttempt = 0;
  }

  private listenerCount(): number {
    let count = 0;
    this.listeners.forEach((listeners) => {
      count += listeners.size;
    });
    return count;
  }
}

export class SessionManager {
  readonly api: ApiClient;
  readonly notifications: NotificationManager;

  private session: Session | null;
  private readonly listeners = new Set<Listener>();
  private refreshFlight: {
    generation: number;
    promise: Promise<boolean>;
  } | null = null;
  private globalErrorHandler?: GlobalErrorHandler;
  private readonly syncChannel: BroadcastChannel | null;
  private readonly syncSource: string;
  private syncTimestamp = 0;
  private syncTimestampSource = "";
  private ready = true;
  private readyTimer: ReturnType<typeof setTimeout> | null = null;
  private sessionGeneration = 0;
  private requestEpoch = 0;
  private sessionCookieTail: Promise<void> = Promise.resolve();

  constructor(
    private readonly baseUrl = "http://localhost:18000",
    private readonly storage: Storage | null = globalThis.sessionStorage,
  ) {
    this.session = this.restore();
    const browserStorage = browserSessionStorage(storage);
    if (browserStorage) {
      const sharedIdentity = readBrowserCookieIdentity();
      if (
        sharedIdentity &&
        (sharedIdentity.tenantId !== (this.session?.tenant.id ?? null) ||
          sharedIdentity.userId !== (this.session?.user.id ?? null))
      ) {
        this.session = null;
        this.storage?.removeItem(storageKey);
      }
    }
    this.syncSource = browserStorage ? globalThis.crypto.randomUUID() : "";
    this.syncChannel =
      this.syncSource && typeof BroadcastChannel !== "undefined"
        ? new BroadcastChannel(sessionChannelName)
        : null;
    if (this.syncChannel) {
      this.syncChannel.onmessage = (event: MessageEvent<unknown>) => {
        const message = sessionSyncMessage(event.data);
        if (!message || message.source === this.syncSource) {
          return;
        }
        if (message.type === "session-request") {
          if (this.session) this.broadcastSession();
          return;
        }
        if (
          message.at < this.syncTimestamp ||
          (message.at === this.syncTimestamp &&
            message.source <= this.syncTimestampSource)
        ) {
          return;
        }
        this.syncTimestamp = message.at;
        this.syncTimestampSource = message.source;
        const sharedIdentity = readBrowserCookieIdentity();
        if (
          sharedIdentity &&
          (message.cookieVersion !== sharedIdentity.version ||
            sharedIdentity.tenantId !== (message.session?.tenant.id ?? null) ||
            sharedIdentity.userId !== (message.session?.user.id ?? null))
        ) {
          return;
        }
        if (message.session && browserRestoreSuppressed()) {
          return;
        }
        this.commit(message.session, false);
      };
    }
    if (!this.session && browserStorage) {
      this.ready = false;
      if (this.syncChannel) {
        queueMicrotask(() => {
          this.syncChannel?.postMessage({
            source: this.syncSource,
            type: "session-request",
          } satisfies SessionRequestMessage);
        });
      }
      this.readyTimer = setTimeout(() => {
        this.readyTimer = null;
        if (browserRestoreSuppressed()) {
          this.finishSessionRestore();
        } else {
          void this.restoreFromCookie();
        }
      }, 500);
    }
    this.api = {
      delete: <T>(path: string, request?: ApiRequest) =>
        this.request<T>("DELETE", path, request),
      get: <T>(path: string, request?: Omit<ApiRequest, "body">) =>
        this.request<T>("GET", path, request),
      patch: <T>(path: string, request?: ApiRequest) =>
        this.request<T>("PATCH", path, request),
      post: <T>(path: string, request?: ApiRequest) =>
        this.request<T>("POST", path, request),
      postStream: (path: string, request?: ApiRequest) =>
        this.requestStream("POST", path, request),
      put: <T>(path: string, request?: ApiRequest) =>
        this.request<T>("PUT", path, request),
      resolve: (path: string) =>
        `${this.baseUrl.replace(/\/+$/, "")}/${path.replace(/^\/+/, "")}`,
    };
    this.notifications = new NotificationManager(this.api, this.getSnapshot);
  }

  setGlobalErrorHandler(handler: GlobalErrorHandler): void {
    this.globalErrorHandler = handler;
  }

  getSnapshot = (): Session | null => this.session;

  getReadySnapshot = (): boolean => this.ready;

  /** 当前会话凭证代际，仅用于将异步结果绑定到其发起时的身份快照。 */
  getSessionGeneration = (): number => this.sessionGeneration;

  subscribe = (listener: Listener): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  runtime(
    navigate: (to: string) => void,
    notificationChannel?: string,
    displayMode: "embedded" | "standalone" = "embedded",
  ): AppRuntime {
    return {
      api: this.api,
      displayMode,
      getSession: this.getSnapshot,
      navigate,
      notifications: this.notifications.bind(notificationChannel),
      subscribeSession: this.subscribe,
    };
  }

  async identify(
    identifier: string,
    password: string,
  ): Promise<PreAuthChallenge> {
    const normalized = identifier.trim().toLowerCase();
    const preAuth = await this.api.post<IdentifyResult>("/user/auth/identify", {
      auth: false,
      body: { identifier: normalized, password },
    });
    const tenantOptions: NonNullable<TenantsResult["items"]> = [];
    const pageSize = 100;
    let page = 1;
    let total = Number.POSITIVE_INFINITY;
    while (tenantOptions.length < total) {
      const result = await this.api.post<TenantsResult>("/user/auth/tenants", {
        auth: false,
        body: { page, pageSize, preAuthToken: preAuth.preAuthToken },
      });
      const items = result.items ?? [];
      tenantOptions.push(...items);
      total = result.total;
      if (items.length === 0) break;
      page++;
    }
    return {
      expiresAt: preAuth.expiresAt,
      identifier: normalized,
      preAuthToken: preAuth.preAuthToken,
      tenants: tenantOptions.map(
        ({ stepUpRequired, tenantId, tenantKind, tenantName }) => ({
          id: tenantId,
          name: tenantName || tenantLabel(tenantId),
          stepUpRequired,
          ...(tenantKind ? { kind: tenantKind } : {}),
        }),
      ),
    };
  }

  async selectTenant(
    challenge: PreAuthChallenge,
    tenantId: string,
    password?: string,
  ): Promise<Session> {
    return this.withSessionCookieLock(async () => {
      const result = await this.api.post<LoginResult>("/user/auth/select", {
        auth: false,
        body: {
          password,
          preAuthToken: challenge.preAuthToken,
          tenantId,
        },
      });
      const tenant =
        challenge.tenants.find(({ id }) => id === tenantId) ??
        ({
          id: tenantId,
          name: tenantLabel(tenantId),
        } satisfies Tenant);
      const next: Session = {
        identifier: challenge.identifier,
        tenant,
        tenants: challenge.tenants,
        token: {
          accessToken: result.accessToken,
          accessExpiresAt: result.accessExpiresAt,
          refreshExpiresAt: result.refreshExpiresAt,
        },
        user: result.user,
      };
      allowBrowserRestore();
      writeBrowserCookieIdentity(next);
      this.commit(next);
      return next;
    });
  }

  async switchTenant(
    tenantId: string,
    identityPassword: string,
    tenantPassword?: string,
  ): Promise<Session> {
    const current = this.session;
    if (!current) {
      throw new ApiError("当前会话已失效，请重新登录", 401);
    }
    const challenge = await this.identify(current.identifier, identityPassword);
    if (!challenge.tenants.some(({ id }) => id === tenantId)) {
      throw new ApiError("该身份无法访问目标组织", 403);
    }
    return this.selectTenant(challenge, tenantId, tenantPassword);
  }

  /**
   * updateProfile 更新当前用户的 self-editable 字段（昵称 / 头像），
   * 成功后把新 user 落回 session，让顶栏等订阅方立即刷新。
   */
  async updateProfile(patch: { displayName?: string }): Promise<User> {
    const current = this.session;
    if (!current) {
      throw new ApiError("当前会话已失效，请重新登录", 401);
    }
    const expected = authenticatedSessionIdentity(
      current,
      this.requestEpoch,
      true,
    )!;
    const user = await this.api.patch<User>("/user/me/profile/update", {
      body: patch,
    });
    const latest = this.session;
    if (
      latest &&
      requestIdentityMatches(latest, expected, this.requestEpoch) &&
      browserCookieIdentityMatches(expected)
    ) {
      // Profile metadata is tab-local until another authoritative session
      // broadcast arrives. Broadcasting a full session here could resurrect an
      // identity that another window has already switched or logged out.
      this.commit({ ...latest, user }, false);
    }
    return user;
  }

  /**
   * sendPasswordChangeCode 由后端解析当前 identity 的已验证手机号并发送改密验证码。
   * 客户端不能指定接收目标，只接收脱敏手机号用于 UI 提示。
   */
  sendPasswordChangeCode(captchaProof: string): Promise<PasswordCodeChallenge> {
    return this.api.post<PasswordCodeChallenge>("/user/me/password/send-code", {
      body: { captchaProof },
    });
  }

  /**
   * changePassword 凭手机短信验证码修改登录密码（ADR-0005 D3）。验证码发到本人已
   * 绑定手机（purpose=reset_password）。后端撤销当前 session 之外的所有会话，当前
   * session 保持有效，本地无需重登。
   */
  async changePassword(payload: ChangePasswordPayload): Promise<void> {
    await this.api.post("/user/me/password/change", { body: payload });
  }

  /**
   * changeTenantPassword 凭旧租户密码修改本租户的 step-up 密码（ADR-0005 D3）。
   * 目标租户由当前会话决定。
   */
  async changeTenantPassword(
    payload: ChangeTenantPasswordPayload,
  ): Promise<void> {
    await this.api.post("/user/me/tenant-password/change", { body: payload });
  }

  // ---------- contact / friends (C 端好友) ----------
  // 列表统一 keyset 分页：pageToken 首页留空，pageSize 1–100；响应 { items, nextToken }，
  // nextToken 空表示到底。api.get 已自动带 Authorization 并解信封取 data。

  /** listFriends 拉好友列表的一页，可按公开姓名或签名过滤。 */
  listFriends(
    pageToken = "",
    pageSize = 20,
    keyword = "",
  ): Promise<Page<Friend>> {
    return this.api.get<Page<Friend>>(
      `/contact/friend/list${pageQuery(pageToken, pageSize, keyword)}`,
    );
  }

  /** listIncoming 拉「别人发给我、待我处理」的请求。 */
  listIncoming(pageToken = "", pageSize = 20): Promise<Page<FriendRequest>> {
    return this.api.get<Page<FriendRequest>>(
      `/contact/request/incoming${pageQuery(pageToken, pageSize)}`,
    );
  }

  /** listOutgoing 拉「我发出、等对方处理」的请求。 */
  listOutgoing(pageToken = "", pageSize = 20): Promise<Page<FriendRequest>> {
    return this.api.get<Page<FriendRequest>>(
      `/contact/request/outgoing${pageQuery(pageToken, pageSize)}`,
    );
  }

  /** sendRequest 发起好友请求：identifier（邮箱或手机号）与 identityId 二选一（identityId 优先）。 */
  sendRequest(target: {
    identifier?: string;
    identityId?: string;
  }): Promise<{ id: string }> {
    return this.api.post<{ id: string }>("/contact/request/send", {
      body: target,
    });
  }

  /** searchUsers 按完整邮箱或手机号精确搜索可添加的对象。 */
  searchUsers(keyword: string): Promise<SearchResult[]> {
    return this.api
      .get<{ items: SearchResult[] }>(
        `/contact/search?keyword=${encodeURIComponent(keyword)}`,
      )
      .then((res) => res.items ?? []);
  }

  /** respond 接受 / 拒绝一条待处理请求。 */
  respond(requestId: string, accept: boolean): Promise<{ accepted: boolean }> {
    return this.api.post<{ accepted: boolean }>(
      `/contact/request/${encodeURIComponent(requestId)}/respond`,
      { body: { accept } },
    );
  }

  /** removeFriend 删除好友（传关系行 friendshipId）。 */
  removeFriend(friendshipId: string): Promise<{ removed: boolean }> {
    return this.api.post<{ removed: boolean }>(
      `/contact/friend/${encodeURIComponent(friendshipId)}/remove`,
    );
  }

  // ---------- registration / tenant (注册 + 创建/加入租户 + 审批) ----------

  /**
   * sendRegisterCode 请求发送验证码（公开端点，无需鉴权）。identifier 可为手机号
   * （11 位中国大陆裸号，不接受 +86 前缀）或邮箱；purpose 指定用途（register /
   * reset_password / bind_phone），默认 register。
   */
  sendRegisterCode(
    identifier: string,
    purpose: VerificationPurpose = "register",
    captchaProof = "",
  ): Promise<VerificationChallenge> {
    return this.api.post<VerificationChallenge>(
      "/user/auth/register/send-code",
      {
        auth: false,
        body: { identifier: identifier.trim(), purpose, captchaProof },
      },
    );
  }

  getCaptchaConfig(): Promise<CaptchaPublicConfig> {
    return this.api.get<CaptchaPublicConfig>("/user/auth/captcha/config", {
      auth: false,
    });
  }

  /**
   * register 提交注册（公开端点）。手机号为主标识并强制短信验证。
   * 成功后端直接签发个人租户会话，这里组装 Session 落库并 commit，实现「注册即
   * 自动登录进个人租户」。
   */
  async register(payload: RegisterPayload): Promise<Session> {
    const phone = payload.phone.trim();
    return this.withSessionCookieLock(async () => {
      const result = await this.api.post<RegisterResult>(
        "/user/auth/register",
        {
          auth: false,
          body: { ...payload, phone },
        },
      );
      const identifier = phone;
      const tenant: Tenant = {
        id: result.tenant.tenantId,
        kind: result.tenant.tenantKind ?? "personal",
        name: result.tenant.tenantName || result.user.displayName || identifier,
      };
      const next: Session = {
        identifier,
        tenant,
        tenants: [tenant],
        token: {
          accessToken: result.accessToken,
          accessExpiresAt: result.accessExpiresAt,
          refreshExpiresAt: result.refreshExpiresAt,
        },
        user: result.user,
      };
      allowBrowserRestore();
      writeBrowserCookieIdentity(next);
      this.commit(next);
      return next;
    });
  }

  /**
   * resetPassword 忘记密码找回（公开端点）：凭手机号 + 短信验证码重置登录密码。
   * 成功后后端会撤销该身份的全部会话，用户需用新密码重新登录。
   */
  async resetPassword(payload: ResetPasswordPayload): Promise<void> {
    await this.api.post("/user/auth/password/reset", {
      auth: false,
      body: { ...payload, phone: payload.phone.trim() },
    });
  }

  /** createTenant 创建 team 租户，返回含邀请码的租户视图。 */
  createTenant(name: string): Promise<TenantSummary> {
    return this.api.post<TenantSummary>("/tenant/create", {
      body: { name: name.trim() },
    });
  }

  /**
   * issueActivation 为管理员开通的成员签发一次性激活令牌（需 users:* scope）。
   * 返回原始 token（仅此一次），前端据此拼激活链接分享给成员。
   */
  issueActivation(
    userId: string,
  ): Promise<{ token: string; expiresAt: string }> {
    return this.api.post<{ token: string; expiresAt: string }>(
      `/user/${userId}/activation`,
    );
  }

  /** activationInfo 按激活 token 查邀请上下文（公开端点，无需鉴权）。 */
  activationInfo(token: string): Promise<ActivationInfo> {
    return this.api.get<ActivationInfo>(
      `/user/auth/activation/${encodeURIComponent(token)}/info`,
      { auth: false },
    );
  }

  /**
   * activate 成员凭激活 token + 绑定验证手机 + 设置密码完成激活（公开端点）。成功后端
   * 签发该租户会话，这里组装 Session 落库并 commit，实现「激活即自动登录」。
   */
  async activate(payload: ActivatePayload): Promise<Session> {
    return this.withSessionCookieLock(async () => {
      const result = await this.api.post<RegisterResult>(
        "/user/auth/activate",
        {
          auth: false,
          body: { ...payload, phone: payload.phone.trim() },
        },
      );
      const tenant: Tenant = {
        id: result.tenant.tenantId,
        kind: result.tenant.tenantKind ?? "team",
        name:
          result.tenant.tenantName ||
          result.user.displayName ||
          result.user.email,
      };
      const next: Session = {
        identifier: result.user.email,
        tenant,
        tenants: [tenant],
        token: {
          accessToken: result.accessToken,
          accessExpiresAt: result.accessExpiresAt,
          refreshExpiresAt: result.refreshExpiresAt,
        },
        user: result.user,
      };
      allowBrowserRestore();
      writeBrowserCookieIdentity(next);
      this.commit(next);
      return next;
    });
  }

  /** currentTenant 查当前登录租户概要（team 租户含邀请码），供管理员查看/分享。 */
  currentTenant(): Promise<TenantSummary> {
    return this.api.get<TenantSummary>("/tenant/current");
  }

  currentTenantAuthPolicy(): Promise<TenantAuthPolicy> {
    return this.api.get<TenantAuthPolicy>("/tenant/auth-policy");
  }

  updateTenantAuthPolicy(sessionTtlDays: number): Promise<TenantAuthPolicy> {
    return this.api.put<TenantAuthPolicy>("/tenant/auth-policy", {
      body: { sessionTtlDays },
    });
  }

  /**
   * updateTenant 改当前租户名（组织设置页）。后端同一事务内同步根部门名。
   * 成功后把新名字落回 session（当前 tenant 及 tenants 列表内对应项），
   * 让顶栏组织名等订阅方即时刷新。
   */
  async updateTenant(name: string): Promise<TenantSummary> {
    const summary = await this.api.put<TenantSummary>("/tenant/update", {
      body: { name: name.trim() },
    });
    const current = this.session;
    if (current && current.tenant.id === summary.id) {
      this.commit(
        {
          ...current,
          tenant: { ...current.tenant, name: summary.name },
          tenants: current.tenants.map((t) =>
            t.id === summary.id ? { ...t, name: summary.name } : t,
          ),
        },
        false,
      );
    }
    return summary;
  }

  /** lookupInvite 按邀请码查目标租户（展示组织名）。 */
  lookupInvite(code: string): Promise<TenantSummary> {
    return this.api.get<TenantSummary>(
      `/tenant/invite/lookup?code=${encodeURIComponent(code.trim())}`,
    );
  }

  /** requestJoin 按邀请码发起加入申请。 */
  requestJoin(code: string, note = ""): Promise<JoinRequest> {
    return this.api.post<JoinRequest>("/tenant/join/request", {
      body: { code: code.trim(), note },
    });
  }

  createTenantTicket(
    payload: CreateTenantTicketPayload,
  ): Promise<TenantTicket> {
    return this.api.post<TenantTicket>("/tenant/tickets", {
      body: {
        body: payload.body.trim(),
        category: payload.category,
        ...(payload.requestType ? { requestType: payload.requestType } : {}),
        ...(payload.requestedDriveQuotaBytes === undefined
          ? {}
          : { requestedDriveQuotaBytes: payload.requestedDriveQuotaBytes }),
        ...(payload.requestedPlanCode
          ? { requestedPlanCode: payload.requestedPlanCode.trim() }
          : {}),
        ...(payload.requestedSeats === undefined
          ? {}
          : { requestedSeats: payload.requestedSeats }),
        ...(payload.requestedTenantName
          ? { requestedTenantName: payload.requestedTenantName.trim() }
          : {}),
        subject: payload.subject.trim(),
      },
    });
  }

  getTenantBilling(signal?: AbortSignal): Promise<TenantBillingOverview> {
    return this.api.get<TenantBillingOverview>(
      "/tenant/billing",
      signal ? { signal } : undefined,
    );
  }

  getTenantUsage(signal?: AbortSignal): Promise<TenantUsageSummary> {
    return this.api.get<TenantUsageSummary>(
      "/tenant/usage",
      signal ? { signal } : undefined,
    );
  }

  getDriveLimitSummary(signal?: AbortSignal): Promise<DriveLimitSummary> {
    return this.api.get<DriveLimitSummary>(
      "/drive/limit",
      signal ? { signal } : undefined,
    );
  }

  listTenantTickets(
    page = 1,
    pageSize = 20,
    signal?: AbortSignal,
  ): Promise<OffsetPage<TenantTicket>> {
    const query = new URLSearchParams({
      page: String(page),
      pageSize: String(pageSize),
    });
    return this.api.get<OffsetPage<TenantTicket>>(
      `/tenant/tickets?${query.toString()}`,
      signal ? { signal } : undefined,
    );
  }

  listApprovals(
    view: ApprovalView,
    options: {
      page?: number;
      pageSize?: number;
      signal?: AbortSignal;
      status?: ApprovalStatus;
      templateKey?: string;
      targetType?: string;
      targetId?: string;
    } = {},
  ): Promise<OffsetPage<ApprovalInstance>> {
    const query = new URLSearchParams({
      page: String(options.page ?? 1),
      pageSize: String(options.pageSize ?? 20),
      view,
    });
    if (options.status) query.set("status", options.status);
    if (options.templateKey) query.set("templateKey", options.templateKey);
    if (options.targetType) query.set("targetType", options.targetType);
    if (options.targetId) query.set("targetId", options.targetId);
    return this.api.get<OffsetPage<ApprovalInstance>>(
      `/approvals/mine?${query.toString()}`,
      options.signal ? { signal: options.signal } : undefined,
    );
  }

  listApprovalTemplates(
    signal?: AbortSignal,
  ): Promise<{ items: ApprovalSubmissionTemplate[] }> {
    return this.api.get<{ items: ApprovalSubmissionTemplate[] }>(
      "/approvals/templates",
      signal ? { signal } : undefined,
    );
  }

  submitApproval(input: SubmitApprovalInput): Promise<ApprovalInstance> {
    return this.api.post<ApprovalInstance>("/approvals", {
      body: {
        idempotencyKey: input.idempotencyKey,
        payload: input.payload,
        reason: input.reason.trim(),
        templateKey: input.templateKey,
      },
    });
  }

  getApproval(
    approvalId: string,
    signal?: AbortSignal,
  ): Promise<ApprovalInstance> {
    return this.api.get<ApprovalInstance>(
      `/approvals/${encodeURIComponent(approvalId)}`,
      signal ? { signal } : undefined,
    );
  }

  decideApproval(
    approvalId: string,
    decision: ApprovalDecisionValue,
    comment = "",
  ): Promise<ApprovalInstance> {
    return this.api.post<ApprovalInstance>(
      `/approvals/${encodeURIComponent(approvalId)}/decision`,
      { body: { comment: comment.trim(), decision } },
    );
  }

  cancelApproval(approvalId: string): Promise<ApprovalInstance> {
    return this.api.post<ApprovalInstance>(
      `/approvals/${encodeURIComponent(approvalId)}/cancel`,
      { body: {} },
    );
  }

  // ---------- tenant FaaS (ADR-0040) ----------

  listFaaSBundleMarket(
    keyword = "",
    page = 1,
    pageSize = 20,
    signal?: AbortSignal,
  ): Promise<OffsetPage<FaaSBundle>> {
    const query = new URLSearchParams({
      page: String(page),
      pageSize: String(pageSize),
    });
    if (keyword.trim()) query.set("keyword", keyword.trim());
    return this.api.get<OffsetPage<FaaSBundle>>(
      `/faas/bundle/market/?${query.toString()}`,
      signal ? { signal } : undefined,
    );
  }

  listSkillMarket(
    keyword = "",
    page = 1,
    pageSize = 100,
    signal?: AbortSignal,
    view?: "catalog" | "installed" | "authored",
    scope?: "all" | "tenant" | "global",
  ): Promise<OffsetPage<SkillMarketItem>> {
    const query = new URLSearchParams({
      page: String(page),
      pageSize: String(pageSize),
    });
    if (keyword.trim()) query.set("keyword", keyword.trim());
    if (view) query.set("view", view);
    if (scope) query.set("scope", scope);
    return this.api.get<OffsetPage<SkillMarketItem>>(
      `/faas/skill/market?${query.toString()}`,
      signal ? { signal } : undefined,
    );
  }

  listSkillDrafts(
    signal?: AbortSignal,
    offset = 0,
  ): Promise<{ items: SkillDraftSummary[] }> {
    return this.api.get(
      `/faas/skill/drafts?limit=100&offset=${offset}`,
      signal ? { signal } : undefined,
    );
  }

  getSkillDraft(id: string, signal?: AbortSignal): Promise<SkillDraft> {
    return this.api.get(
      `/faas/skill/drafts/${encodeURIComponent(id)}`,
      signal ? { signal } : undefined,
    );
  }

  saveSkillDraft(
    id: string,
    body: SaveSkillDraftPayload,
  ): Promise<SkillDraftReceipt> {
    return this.api.put(`/faas/skill/drafts/${encodeURIComponent(id)}`, {
      body,
      onError: () => undefined,
    });
  }

  freezeSkillDraft(
    id: string,
    body: FreezeSkillDraftPayload,
  ): Promise<SkillDraftReceipt> {
    return this.api.post(
      `/faas/skill/drafts/${encodeURIComponent(id)}/versions`,
      { body, onError: () => undefined },
    );
  }

  getSkillDraftOperation(id: string): Promise<SkillDraftReceipt> {
    return this.api.get(
      `/faas/skill/draft-operations/${encodeURIComponent(id)}`,
      { onError: () => undefined },
    );
  }

  inspectSkillArchive(
    file: File,
    signal?: AbortSignal,
  ): Promise<SkillArchiveInspection> {
    const body = new FormData();
    body.append("archive", file);
    return this.api.post("/faas/skill/archives/inspect", {
      body,
      ...(signal ? { signal } : {}),
      onError: () => undefined,
    });
  }

  publishSkillBundle(
    bundleId: string,
    payload: PublishSkillBundlePayload,
  ): Promise<FaaSBundle> {
    return this.api.put<FaaSBundle>(
      `/faas/skill/bundle/${encodeURIComponent(bundleId)}/publication`,
      { body: payload },
    );
  }

  listSkillPublicationVersions(
    bundleId: string,
    signal?: AbortSignal,
  ): Promise<{ versions: SkillPublicationVersion[] }> {
    return this.api.get<{ versions: SkillPublicationVersion[] }>(
      `/faas/skill/bundle/${encodeURIComponent(bundleId)}/publication/versions`,
      signal ? { signal } : undefined,
    );
  }

  getSkillTenantPolicy(
    surface = "ai",
    signal?: AbortSignal,
  ): Promise<SkillTenantPolicy> {
    return this.api.get<SkillTenantPolicy>(
      `/faas/skill/policy?surface=${encodeURIComponent(surface)}`,
      signal ? { signal } : undefined,
    );
  }

  saveSkillTenantPolicy(input: {
    allowedSkillRefs: string[];
    enabledByDefault: boolean;
    expectedGeneration: number;
    exposureMode: SkillExposureMode;
    surface?: string;
  }): Promise<SkillTenantPolicy> {
    return this.api.put<SkillTenantPolicy>("/faas/skill/policy", {
      body: { ...input, surface: input.surface ?? "ai" },
    });
  }

  getUserSkillPreferences(
    surface = "ai",
    signal?: AbortSignal,
  ): Promise<UserSkillPreferences> {
    return this.api.get<UserSkillPreferences>(
      `/faas/skill/preferences/me?surface=${encodeURIComponent(surface)}`,
      signal ? { signal } : undefined,
    );
  }

  saveUserSkillPreferences(input: {
    disabledSkillRefs: string[];
    enabledSkillRefs: string[];
    expectedGeneration: number;
    exposureMode: SkillExposureMode;
    selectedSkillRefs: string[];
    surface?: string;
  }): Promise<UserSkillPreferences> {
    return this.api.put<UserSkillPreferences>("/faas/skill/preferences/me", {
      body: { ...input, surface: input.surface ?? "ai" },
    });
  }

  previewEffectiveSkills(
    surface = "ai",
    signal?: AbortSignal,
  ): Promise<SkillContextPlan> {
    return this.api.get<SkillContextPlan>(
      `/faas/skill/effective?surface=${encodeURIComponent(surface)}`,
      signal ? { signal } : undefined,
    );
  }

  getFaaSBundle(
    bundleId: string,
    signal?: AbortSignal,
  ): Promise<FaaSBundleDetail> {
    return this.api.get<FaaSBundleDetail>(
      `/faas/bundle/market/${encodeURIComponent(bundleId)}`,
      signal ? { signal } : undefined,
    );
  }

  importFaaSBundlePackage(archive: File): Promise<FaaSBundleImportResult> {
    const body = new FormData();
    body.append("archive", archive, archive.name);
    return this.api.post<FaaSBundleImportResult>(
      "/faas/bundle/package/import",
      { body },
    );
  }

  inspectFaaSBundlePackage(
    archive: File,
  ): Promise<{ content: string; path: string }> {
    const body = new FormData();
    body.append("archive", archive, archive.name);
    return this.api.post<{ content: string; path: string }>(
      "/faas/bundle/package/inspect",
      { body },
    );
  }

  getFaaSBundleMember(
    bundleId: string,
    bundleVersionId: string,
    functionVersionId: string,
    signal?: AbortSignal,
  ): Promise<FaaSBundleMemberDetail> {
    return this.api.get<FaaSBundleMemberDetail>(
      `/faas/bundle/market/${encodeURIComponent(bundleId)}/version/${encodeURIComponent(bundleVersionId)}/member/${encodeURIComponent(functionVersionId)}`,
      signal ? { signal } : undefined,
    );
  }

  listFaaSBundleInstallations(
    signal?: AbortSignal,
  ): Promise<{ items: FaaSBundleInstallation[] }> {
    return this.api.get<{ items: FaaSBundleInstallation[] }>(
      "/faas/bundle/installation/list",
      signal ? { signal } : undefined,
    );
  }

  listFaaSBundleInstallationSummaries(
    signal?: AbortSignal,
  ): Promise<{ items: FaaSBundleInstallationSummary[] }> {
    return this.api.get(
      "/faas/bundle/installation/summary",
      signal ? { signal } : undefined,
    );
  }

  listOwnFaaSBundleInstallationRequests(
    page = 1,
    pageSize = 20,
    signal?: AbortSignal,
    latestForBundles?: string[],
  ): Promise<OffsetPage<FaaSBundleInstallationRequestSummary>> {
    const query = new URLSearchParams({
      page: String(page),
      pageSize: String(pageSize),
    });
    if (latestForBundles?.length)
      query.set("latestForBundles", latestForBundles.join(","));
    return this.api.get(
      `/faas/bundle/installation/requests/mine?${query}`,
      signal ? { signal } : undefined,
    );
  }

  installFaaSBundle(
    bundleId: string,
    bundleVersionId: string,
    reason = "",
  ): Promise<FaaSBundleInstallation> {
    return this.api.post<FaaSBundleInstallation>(
      `/faas/bundle/${encodeURIComponent(bundleId)}/install`,
      { body: { bundleVersionId, reason: reason.trim() } },
    );
  }

  requestFaaSBundleInstallation(
    bundleId: string,
    input: {
      bundleVersionId: string;
      idempotencyKey: string;
      reason: string;
    },
  ): Promise<FaaSBundleInstallationRequest> {
    return this.api.post<FaaSBundleInstallationRequest>(
      `/faas/bundle/${encodeURIComponent(bundleId)}/installation-request`,
      {
        body: {
          bundleVersionId: input.bundleVersionId,
          idempotencyKey: input.idempotencyKey,
          reason: input.reason.trim(),
        },
      },
    );
  }

  updateFaaSBundleInstallation(
    installationId: string,
    action: "disable" | "enable" | "rollback" | "uninstall" | "upgrade",
    input: {
      bundleVersionId?: string;
      expectedGeneration: number;
      reason?: string;
    },
  ): Promise<FaaSBundleInstallation> {
    return this.api.post<FaaSBundleInstallation>(
      `/faas/bundle/installation/${encodeURIComponent(installationId)}/${action}`,
      {
        body: {
          expectedGeneration: input.expectedGeneration,
          ...(input.bundleVersionId
            ? { bundleVersionId: input.bundleVersionId }
            : {}),
          ...(input.reason?.trim() ? { reason: input.reason.trim() } : {}),
        },
      },
    );
  }

  listFaaSBundleInstallationHistory(
    installationId: string,
    signal?: AbortSignal,
  ): Promise<{ items: FaaSBundleInstallationHistory[] }> {
    return this.api.get<{ items: FaaSBundleInstallationHistory[] }>(
      `/faas/bundle/installation/${encodeURIComponent(installationId)}/history`,
      signal ? { signal } : undefined,
    );
  }

  getFaaSBundleInstallationConfiguration(
    installationId: string,
    signal?: AbortSignal,
  ): Promise<FaaSInstallationConfiguration> {
    return this.api.get<FaaSInstallationConfiguration>(
      `/faas/bundle/installation/${encodeURIComponent(installationId)}/configuration`,
      signal ? { signal } : undefined,
    );
  }

  saveFaaSBundleInstallationConfiguration(
    installationId: string,
    input: {
      expectedConfigGeneration: number;
      reason: string;
      removeOverrides?: string[];
      secrets?: Record<string, string>;
      values?: Record<string, unknown>;
    },
  ): Promise<FaaSInstallationConfiguration> {
    return this.api.put<FaaSInstallationConfiguration>(
      `/faas/bundle/installation/${encodeURIComponent(installationId)}/configuration`,
      {
        body: {
          expectedConfigGeneration: input.expectedConfigGeneration,
          reason: input.reason.trim(),
          removeOverrides: input.removeOverrides ?? [],
          secrets: input.secrets ?? {},
          values: input.values ?? {},
        },
      },
    );
  }

  listFaaSFunctions(
    page = 1,
    pageSize = 20,
    signal?: AbortSignal,
  ): Promise<OffsetPage<FaaSFunction>> {
    const query = new URLSearchParams({
      page: String(page),
      pageSize: String(pageSize),
    });
    return this.api.get<OffsetPage<FaaSFunction>>(
      `/faas/function/list?${query.toString()}`,
      signal ? { signal } : undefined,
    );
  }

  createFaaSFunction(
    payload: CreateFaaSFunctionPayload,
  ): Promise<FaaSFunction> {
    return this.api.post<FaaSFunction>("/faas/function/create", {
      body: {
        displayName: payload.displayName.trim(),
        externalAccessEnabled: payload.externalAccessEnabled,
        slug: payload.slug.trim(),
        ...(payload.description?.trim()
          ? { description: payload.description.trim() }
          : {}),
      },
    });
  }

  getFaaSFunction(
    functionId: string,
    signal?: AbortSignal,
  ): Promise<FaaSFunction> {
    return this.api.get<FaaSFunction>(
      `/faas/function/${encodeURIComponent(functionId)}/get`,
      signal ? { signal } : undefined,
    );
  }

  setFaaSFunctionStatus(
    functionId: string,
    status: FaaSFunction["status"],
  ): Promise<FaaSFunction> {
    return this.api.put<FaaSFunction>(
      `/faas/function/${encodeURIComponent(functionId)}/status`,
      { body: { status } },
    );
  }

  setFaaSFunctionExternalAccess(
    functionId: string,
    enabled: boolean,
  ): Promise<FaaSFunction> {
    return this.api.put<FaaSFunction>(
      `/faas/function/${encodeURIComponent(functionId)}/external-access`,
      { body: { enabled } },
    );
  }

  listFaaSVersions(
    functionId: string,
    page = 1,
    pageSize = 50,
    signal?: AbortSignal,
  ): Promise<OffsetPage<FaaSVersion>> {
    const query = new URLSearchParams({
      page: String(page),
      pageSize: String(pageSize),
    });
    return this.api.get<OffsetPage<FaaSVersion>>(
      `/faas/function/${encodeURIComponent(functionId)}/version/list?${query.toString()}`,
      signal ? { signal } : undefined,
    );
  }

  publishFaaSDenoSourceVersion(
    functionId: string,
    payload: PublishFaaSDenoSourcePayload,
  ): Promise<FaaSVersion> {
    return this.api.post<FaaSVersion>(
      `/faas/function/${encodeURIComponent(functionId)}/version/deno-source/publish`,
      { body: payload },
    );
  }

  getFaaSDenoVersionSource(
    functionId: string,
    versionId: string,
    signal?: AbortSignal,
  ): Promise<FaaSDenoVersionSource> {
    return this.api.get<FaaSDenoVersionSource>(
      `/faas/function/${encodeURIComponent(functionId)}/version/${encodeURIComponent(versionId)}/source`,
      signal ? { signal } : undefined,
    );
  }

  activateFaaSVersion(
    functionId: string,
    versionId: string,
  ): Promise<FaaSFunction> {
    return this.api.post<FaaSFunction>(
      `/faas/function/${encodeURIComponent(functionId)}/version/${encodeURIComponent(versionId)}/activate`,
    );
  }

  retireFaaSVersion(
    functionId: string,
    versionId: string,
  ): Promise<FaaSVersion> {
    return this.api.post<FaaSVersion>(
      `/faas/function/${encodeURIComponent(functionId)}/version/${encodeURIComponent(versionId)}/retire`,
    );
  }

  invokeFaaSFunction(
    functionId: string,
    payload: InvokeFaaSFunctionPayload,
  ): Promise<FaaSInvocation> {
    return this.api.post<FaaSInvocation>(
      `/faas/function/${encodeURIComponent(functionId)}/invoke`,
      { body: payload },
    );
  }

  listFaaSInvocations(
    functionId = "",
    page = 1,
    pageSize = 20,
    signal?: AbortSignal,
  ): Promise<OffsetPage<FaaSInvocation>> {
    const query = new URLSearchParams({
      page: String(page),
      pageSize: String(pageSize),
    });
    if (functionId) query.set("functionId", functionId);
    return this.api.get<OffsetPage<FaaSInvocation>>(
      `/faas/invocation/list?${query.toString()}`,
      signal ? { signal } : undefined,
    );
  }

  cancelFaaSInvocation(invocationId: string): Promise<FaaSInvocation> {
    return this.api.post<FaaSInvocation>(
      `/faas/invocation/${encodeURIComponent(invocationId)}/cancel`,
    );
  }

  listFaaSFunctionAccessTokens(
    functionId: string,
    signal?: AbortSignal,
  ): Promise<FaaSFunctionAccessToken[]> {
    return this.api
      .get<{ items: FaaSFunctionAccessToken[] }>(
        `/faas/function/${encodeURIComponent(functionId)}/token/list`,
        signal ? { signal } : undefined,
      )
      .then(({ items }) => items ?? []);
  }

  createFaaSFunctionAccessToken(
    functionId: string,
    payload: CreateFaaSFunctionAccessTokenPayload,
  ): Promise<IssuedFaaSFunctionAccessToken> {
    return this.api.post<IssuedFaaSFunctionAccessToken>(
      `/faas/function/${encodeURIComponent(functionId)}/token/create`,
      {
        body: {
          capabilities: payload.capabilities,
          name: payload.name.trim(),
          ...(payload.expiresAt ? { expiresAt: payload.expiresAt } : {}),
        },
      },
    );
  }

  revokeFaaSFunctionAccessToken(
    functionId: string,
    tokenId: string,
  ): Promise<FaaSFunctionAccessToken> {
    return this.api.post<FaaSFunctionAccessToken>(
      `/faas/function/${encodeURIComponent(functionId)}/token/${encodeURIComponent(tokenId)}/revoke`,
    );
  }

  listFaaSGrantableTools(signal?: AbortSignal): Promise<FaaSGrantableTool[]> {
    return this.api
      .get<{ tools: FaaSGrantableTool[] }>(
        "/faas/tool-grants/catalog",
        signal ? { signal } : undefined,
      )
      .then(({ tools }) => tools ?? []);
  }

  getFaaSOAuthClientToolPolicy(
    clientId: string,
    signal?: AbortSignal,
  ): Promise<FaaSOAuthClientToolPolicy> {
    return this.api.get<FaaSOAuthClientToolPolicy>(
      `/faas/oauth-clients/${encodeURIComponent(clientId)}/tool-policy`,
      signal ? { signal } : undefined,
    );
  }

  replaceFaaSOAuthClientToolGrants(
    clientId: string,
    payload: ReplaceFaaSOAuthClientToolGrantsPayload,
  ): Promise<FaaSOAuthClientToolPolicy> {
    return this.api.put<FaaSOAuthClientToolPolicy>(
      `/faas/oauth-clients/${encodeURIComponent(clientId)}/tool-policy`,
      { body: payload },
    );
  }

  // ---------- application / app center (应用中心) ----------
  // 只读 list 登录即可（后端按 status=active + required_scope 过滤）；admin/list 与
  // 增删改需 apps:* scope（tenant_admin 默认拥有）。

  /** listApplications 逐页拉完可见应用，供 shell 动态路由注册。每次请求仍受分页上限约束。 */
  async listApplications(
    signal?: AbortSignal,
    expectedIdentity?: { tenantId: string; userId: string },
  ): Promise<Application[]> {
    const pageSize = 100;
    const items: Application[] = [];
    for (let page = 1; ; page++) {
      this.assertApplicationListIdentity(signal, expectedIdentity);
      const result = await this.listApplicationPage(page, pageSize, "", signal);
      this.assertApplicationListIdentity(signal, expectedIdentity);
      items.push(...(result.items ?? []));
      if (items.length >= result.total || result.items.length === 0)
        return items;
    }
  }

  listApplicationPage(
    page = 1,
    pageSize = 20,
    keyword = "",
    signal?: AbortSignal,
  ): Promise<OffsetPage<Application>> {
    const query = offsetPageQuery(page, pageSize, keyword);
    return this.api.get<OffsetPage<Application>>(
      `/application/list${query}`,
      signal ? { signal } : undefined,
    );
  }

  /** listAllApplications 分页拉本租户全部安装（含 disabled，需 apps:* scope）。 */
  listAllApplications(
    page = 1,
    pageSize = 20,
    keyword = "",
  ): Promise<OffsetPage<Application>> {
    const query = offsetPageQuery(page, pageSize, keyword);
    return this.api.get<OffsetPage<Application>>(
      `/application/admin/list${query}`,
    );
  }

  /** listMarketApplications 浏览系统级应用目录，可按名称/slug/id 搜索。 */
  listMarketApplications(
    page = 1,
    pageSize = 20,
    keyword = "",
  ): Promise<OffsetPage<CatalogApplication>> {
    const query = offsetPageQuery(page, pageSize, keyword);
    return this.api.get<OffsetPage<CatalogApplication>>(
      `/application/market/list${query}`,
    );
  }

  getMarketApplication(id: string): Promise<CatalogApplication> {
    return this.api.get<CatalogApplication>(
      `/application/market/${encodeURIComponent(id)}/get`,
    );
  }

  listApplicationVersions(catalogId: string): Promise<ApplicationVersion[]> {
    return this.api.get<ApplicationVersion[]>(
      `/application/market/${encodeURIComponent(catalogId)}/versions`,
    );
  }

  /** installApplication 把系统目录应用的固定版本安装到当前租户。 */
  installApplication(
    catalogId: string,
    input: {
      versionId?: string;
      grantedCapabilities?: string[];
      autoUpdatePolicy?: "manual" | "patch" | "minor";
    } = {},
  ): Promise<Application> {
    return this.api.post<Application>(
      `/application/${encodeURIComponent(catalogId)}/install`,
      { body: input },
    );
  }

  changeApplicationVersion(
    installationId: string,
    input: {
      versionId: string;
      grantedCapabilities?: string[];
      reason: string;
    },
  ): Promise<Application> {
    return this.api.post<Application>(
      `/application/${encodeURIComponent(installationId)}/version`,
      { body: input },
    );
  }

  /** setApplicationStatus 启用 / 停用租户安装（需 apps:* scope）。 */
  setApplicationStatus(
    id: string,
    op: "enable" | "disable",
  ): Promise<Application> {
    return this.api.post<Application>(
      `/application/${encodeURIComponent(id)}/status`,
      { body: { op } },
    );
  }

  setTenantApplicationLaunchTarget(
    id: string,
    targetKey: string,
  ): Promise<Application> {
    return this.api.put<Application>(
      `/application/${encodeURIComponent(id)}/launch-target/tenant`,
      { body: { targetKey } },
    );
  }

  setUserApplicationLaunchTarget(
    id: string,
    targetKey: string,
  ): Promise<Application> {
    return this.api.put<Application>(
      `/application/${encodeURIComponent(id)}/launch-target/me`,
      { body: { targetKey } },
    );
  }

  /** deleteApplication 从当前租户卸载应用（需 apps:* scope）。 */
  deleteApplication(id: string): Promise<{ deleted: boolean }> {
    return this.api.delete<{ deleted: boolean }>(
      `/application/${encodeURIComponent(id)}/delete`,
    );
  }

  // ---------- tenant developer applications (ADR-0017) ----------

  listDeveloperApplications(
    page = 1,
    pageSize = 20,
    keyword = "",
  ): Promise<OffsetPage<DeveloperApplication>> {
    const query = offsetPageQuery(page, pageSize, keyword);
    return this.api.get<OffsetPage<DeveloperApplication>>(
      `/developer/applications${query}`,
    );
  }

  createDeveloperApplication(
    payload: CreateDeveloperApplicationPayload,
  ): Promise<DeveloperApplication> {
    return this.api.post<DeveloperApplication>("/developer/applications", {
      body: payload,
    });
  }

  getDeveloperApplication(appId: string): Promise<DeveloperApplication> {
    return this.api.get<DeveloperApplication>(
      `/developer/applications/${encodeURIComponent(appId)}`,
    );
  }

  updateDeveloperApplication(
    appId: string,
    payload: UpdateDeveloperApplicationPayload,
  ): Promise<DeveloperApplication> {
    return this.api.patch<DeveloperApplication>(
      `/developer/applications/${encodeURIComponent(appId)}`,
      { body: payload },
    );
  }

  disableDeveloperApplication(appId: string): Promise<DeveloperApplication> {
    return this.api.post<DeveloperApplication>(
      `/developer/applications/${encodeURIComponent(appId)}/disable`,
    );
  }

  listDeveloperApplicationRoutes(
    appId: string,
  ): Promise<DeveloperApplicationRoute[]> {
    return this.api.get<DeveloperApplicationRoute[]>(
      `/developer/applications/${encodeURIComponent(appId)}/routes`,
    );
  }

  upsertDeveloperApplicationRoute(
    appId: string,
    kind: DeveloperApplicationRouteKind,
    payload: UpsertDeveloperApplicationRoutePayload,
  ): Promise<DeveloperApplicationRoute> {
    return this.api.put<DeveloperApplicationRoute>(
      `/developer/applications/${encodeURIComponent(appId)}/routes/${encodeURIComponent(kind)}`,
      { body: payload },
    );
  }

  disableDeveloperApplicationRoute(
    appId: string,
    kind: DeveloperApplicationRouteKind,
  ): Promise<DeveloperApplicationRoute> {
    return this.api.post<DeveloperApplicationRoute>(
      `/developer/applications/${encodeURIComponent(appId)}/routes/${encodeURIComponent(kind)}/disable`,
    );
  }

  getDeveloperApplicationSite(appId: string): Promise<StaticSite> {
    return this.api.get<StaticSite>(
      `/developer/applications/${encodeURIComponent(appId)}/site`,
    );
  }

  createDeveloperApplicationSite(
    appId: string,
    payload: { name: string; spaFallback: boolean },
  ): Promise<StaticSite> {
    return this.api.post<StaticSite>(
      `/developer/applications/${encodeURIComponent(appId)}/site`,
      { body: payload },
    );
  }

  setDeveloperApplicationSiteStatus(
    appId: string,
    status: StaticSite["status"],
  ): Promise<StaticSite> {
    return this.api.put<StaticSite>(
      `/developer/applications/${encodeURIComponent(appId)}/site/status`,
      { body: { status } },
    );
  }

  listDeveloperApplicationSiteReleases(
    appId: string,
    page = 1,
    pageSize = 50,
  ): Promise<StaticSiteReleasePage> {
    return this.api.get<StaticSiteReleasePage>(
      `/developer/applications/${encodeURIComponent(appId)}/site/releases?page=${page}&pageSize=${pageSize}`,
    );
  }

  async uploadDeveloperApplicationSiteRelease(
    appId: string,
    version: string,
    archive: File,
  ): Promise<StaticSiteRelease> {
    const raw = await archive.arrayBuffer();
    const digest = await globalThis.crypto.subtle.digest("SHA-256", raw);
    const archiveDigest = `sha256:${hexBytes(new Uint8Array(digest))}`;
    const contentType = staticSiteArchiveContentType(archive);
    const session = await this.api.post<StaticSiteUploadSession>(
      `/developer/applications/${encodeURIComponent(appId)}/site/uploads`,
      {
        body: {
          archiveDigest,
          archiveSize: archive.size,
          contentType,
          version,
        },
      },
    );
    const uploaded = await fetch(session.uploadUrl, {
      body: archive,
      headers: session.headers,
      method: "PUT",
    });
    if (!uploaded.ok) {
      throw new ApiError(
        `静态站点制品上传失败 (${uploaded.status})`,
        uploaded.status,
      );
    }
    return this.api.post<StaticSiteRelease>(
      `/developer/applications/${encodeURIComponent(appId)}/site/uploads/${encodeURIComponent(session.upload.id)}/complete`,
    );
  }

  activateDeveloperApplicationSiteRelease(
    appId: string,
    releaseId: string,
    expectedActiveReleaseId: string,
  ): Promise<StaticSite> {
    return this.api.post<StaticSite>(
      `/developer/applications/${encodeURIComponent(appId)}/site/releases/${encodeURIComponent(releaseId)}/activate`,
      { body: { expectedActiveReleaseId } },
    );
  }

  listManagedAppConfigEnvironments(
    appId: string,
  ): Promise<{ items: ManagedAppConfigEnvironment[] }> {
    return this.api.get<{ items: ManagedAppConfigEnvironment[] }>(
      `/app-config/applications/${encodeURIComponent(appId)}/environments`,
    );
  }

  getManagedAppConfig(
    appId: string,
    environment: string,
  ): Promise<ManagedAppConfigSnapshot> {
    return this.api.get<ManagedAppConfigSnapshot>(
      `/app-config/applications/${encodeURIComponent(appId)}/environments/${encodeURIComponent(environment)}`,
    );
  }

  saveManagedAppConfig(
    appId: string,
    environment: string,
    payload: SaveManagedAppConfigPayload,
  ): Promise<ManagedAppConfigSnapshot> {
    return this.api.put<ManagedAppConfigSnapshot>(
      `/app-config/applications/${encodeURIComponent(appId)}/environments/${encodeURIComponent(environment)}`,
      { body: payload },
    );
  }

  listOAuthClients(appId: string): Promise<OAuthClient[]> {
    return this.api.get<OAuthClient[]>(
      `/developer/applications/${encodeURIComponent(appId)}/clients`,
    );
  }

  createOAuthClient(
    appId: string,
    payload: CreateOAuthClientPayload,
  ): Promise<OAuthClient> {
    return this.api.post<OAuthClient>(
      `/developer/applications/${encodeURIComponent(appId)}/clients`,
      { body: payload },
    );
  }

  updateOAuthClient(
    oauthClientId: string,
    payload: UpdateOAuthClientPayload,
  ): Promise<OAuthClient> {
    return this.api.patch<OAuthClient>(
      `/developer/clients/${encodeURIComponent(oauthClientId)}`,
      { body: payload },
    );
  }

  disableOAuthClient(oauthClientId: string): Promise<OAuthClient> {
    return this.api.post<OAuthClient>(
      `/developer/clients/${encodeURIComponent(oauthClientId)}/disable`,
    );
  }

  listOAuthClientSecrets(oauthClientId: string): Promise<OAuthClientSecret[]> {
    return this.api.get<OAuthClientSecret[]>(
      `/developer/clients/${encodeURIComponent(oauthClientId)}/secrets`,
    );
  }

  createOAuthClientSecret(
    oauthClientId: string,
    payload: CreateOAuthClientSecretPayload = {},
  ): Promise<CreateOAuthClientSecretResponse> {
    return this.api.post<CreateOAuthClientSecretResponse>(
      `/developer/clients/${encodeURIComponent(oauthClientId)}/secrets`,
      { body: payload },
    );
  }

  revokeOAuthClientSecret(
    oauthClientId: string,
    secretId: string,
  ): Promise<RevokeOAuthClientSecretResponse> {
    return this.api.delete<RevokeOAuthClientSecretResponse>(
      `/developer/clients/${encodeURIComponent(oauthClientId)}/secrets/${encodeURIComponent(secretId)}`,
    );
  }

  listOAuthRedirectUris(oauthClientId: string): Promise<OAuthRedirectUri[]> {
    return this.api.get<OAuthRedirectUri[]>(
      `/developer/clients/${encodeURIComponent(oauthClientId)}/redirect-uris`,
    );
  }

  createOAuthRedirectUri(
    oauthClientId: string,
    payload: CreateOAuthRedirectUriPayload,
  ): Promise<OAuthRedirectUri> {
    return this.api.post<OAuthRedirectUri>(
      `/developer/clients/${encodeURIComponent(oauthClientId)}/redirect-uris`,
      { body: payload },
    );
  }

  deleteOAuthRedirectUri(
    oauthClientId: string,
    redirectUriId: string,
  ): Promise<DeleteOAuthRedirectUriResponse> {
    return this.api.delete<DeleteOAuthRedirectUriResponse>(
      `/developer/clients/${encodeURIComponent(oauthClientId)}/redirect-uris/${encodeURIComponent(redirectUriId)}`,
    );
  }

  listOAuthScopes(): Promise<OAuthScope[]> {
    return this.api.get<OAuthScope[]>("/developer/scopes");
  }

  listDeveloperApplicationScopes(
    appId: string,
  ): Promise<DeveloperApplicationScopeGrant[]> {
    return this.api.get<DeveloperApplicationScopeGrant[]>(
      `/developer/applications/${encodeURIComponent(appId)}/scopes`,
    );
  }

  updateDeveloperApplicationScopes(
    appId: string,
    payload: UpdateDeveloperApplicationScopesPayload,
  ): Promise<DeveloperApplicationScopeGrant[]> {
    return this.api.put<DeveloperApplicationScopeGrant[]>(
      `/developer/applications/${encodeURIComponent(appId)}/scopes`,
      { body: payload },
    );
  }

  submitDeveloperApplicationScopeApprovalRequests(
    appId: string,
    payload: SubmitScopeApprovalRequestsPayload,
  ): Promise<ScopeApprovalRequest[]> {
    return this.api.post<ScopeApprovalRequest[]>(
      `/developer/applications/${encodeURIComponent(appId)}/scope-approval-requests`,
      { body: payload },
    );
  }

  getOAuthAuthorizationDetails(
    query: string,
    signal?: AbortSignal,
  ): Promise<OAuthAuthorizationDetails> {
    return this.api.get<OAuthAuthorizationDetails>(
      `/oauth/authorize${query}`,
      signal ? { signal } : undefined,
    );
  }

  decideOAuthAuthorization(
    payload: OAuthAuthorizationDecisionPayload,
  ): Promise<OAuthAuthorizationDecisionResponse> {
    return this.api.post<OAuthAuthorizationDecisionResponse>(
      "/oauth/authorize/decision",
      { body: payload },
    );
  }

  listOAuthUserAuthorizations(): Promise<OAuthUserAuthorization[]> {
    return this.api.get<OAuthUserAuthorization[]>("/oauth/authorizations");
  }

  revokeOAuthUserAuthorization(clientId: string): Promise<void> {
    return this.api.delete<void>(
      `/oauth/authorizations/${encodeURIComponent(clientId)}`,
    );
  }

  /**
   * uploadAvatar 通过 IAM 创建 Drive Asset，浏览器直传对象存储后由 IAM 完成
   * multipart、引用绑定和头像切换。业务表只保存 asset 引用，不保存裸对象 key。
   */
  async uploadAvatar(file: File): Promise<User> {
    const startedFor = this.session;
    if (!startedFor) {
      throw new ApiError("当前会话已失效，请重新登录", 401);
    }
    const uploadIdentity = authenticatedSessionIdentity(
      startedFor,
      this.requestEpoch,
      true,
    )!;
    const { assetId, headers, uploadUrl } = await this.api.post<{
      assetId: string;
      headers: Record<string, string>;
      uploadUrl: string;
    }>("/user/me/avatar/presign", {
      body: {
        contentType: file.type,
        name: file.name,
        sizeBytes: file.size,
      },
    });

    if (
      !requestIdentityMatches(
        this.session,
        uploadIdentity,
        this.requestEpoch,
      ) ||
      !browserCookieIdentityMatches(uploadIdentity)
    ) {
      throw new DOMException("头像上传期间会话身份已变化", "AbortError");
    }

    const put = await fetch(uploadUrl, {
      body: file,
      headers,
      method: "PUT",
    });
    if (!put.ok) {
      throw new ApiError(`头像上传失败 (${put.status})`, put.status);
    }
    const etag = put.headers.get("ETag") || put.headers.get("etag");
    if (!etag) {
      throw new ApiError("头像上传响应缺少 ETag", 502);
    }
    if (
      !requestIdentityMatches(
        this.session,
        uploadIdentity,
        this.requestEpoch,
      ) ||
      !browserCookieIdentityMatches(uploadIdentity)
    ) {
      throw new DOMException("头像上传期间会话身份已变化", "AbortError");
    }
    const user = await this.api.post<User>(
      `/user/me/avatar/${encodeURIComponent(assetId)}/complete`,
      { body: { etag } },
    );
    const current = this.session;
    if (
      current &&
      requestIdentityMatches(current, uploadIdentity, this.requestEpoch) &&
      browserCookieIdentityMatches(uploadIdentity)
    ) {
      this.commit({ ...current, user }, false);
    }
    return user;
  }

  async logout(): Promise<void> {
    suppressBrowserRestore();
    this.commit(null);
    try {
      await this.withSessionCookieLock(async () => {
        try {
          await this.api.post("/user/auth/logout", {
            auth: false,
          });
        } finally {
          writeBrowserCookieIdentity(null);
        }
      });
    } catch {
      // Local logout must complete even when IAM is unavailable.
    } finally {
      // A login/select operation that held the cookie lock before logout may
      // have committed locally or broadcast after logout was queued. Always
      // publish a newer null tombstone after the cookie mutation completes.
      suppressBrowserRestore();
      this.commit(null);
    }
  }

  private assertApplicationListIdentity(
    signal?: AbortSignal,
    expectedIdentity?: { tenantId: string; userId: string },
  ): void {
    signal?.throwIfAborted();
    if (!expectedIdentity) return;
    if (
      this.session?.tenant.id !== expectedIdentity.tenantId ||
      this.session.user.id !== expectedIdentity.userId
    ) {
      throw new DOMException("应用目录请求身份已变化", "AbortError");
    }
  }

  private async request<T>(
    method: string,
    path: string,
    request: ApiRequest = {},
    retried = false,
    expectedIdentity = authenticatedSessionIdentity(
      this.session,
      this.requestEpoch,
      request.auth,
    ),
  ): Promise<T> {
    if (
      expectedIdentity &&
      (!requestIdentityMatches(
        this.session,
        expectedIdentity,
        this.requestEpoch,
      ) ||
        !browserCookieIdentityMatches(expectedIdentity))
    ) {
      throw new DOMException("请求发起前会话身份已变化", "AbortError");
    }
    const headers = new Headers(request.headers);
    const multipart =
      typeof FormData !== "undefined" && request.body instanceof FormData;
    headers.set("Accept", "application/json");
    headers.set("X-Api-Version", "v1");
    if (request.body !== undefined && !multipart) {
      headers.set("Content-Type", "application/json");
    }
    if (request.auth !== false && this.session?.token.accessToken) {
      headers.set("Authorization", `Bearer ${this.session.token.accessToken}`);
    }

    const init: RequestInit = {
      credentials: "include",
      headers,
      method,
    };
    if (request.body !== undefined) {
      init.body = multipart
        ? (request.body as FormData)
        : JSON.stringify(request.body);
    }
    if (request.signal !== undefined) init.signal = request.signal;
    try {
      const response = await fetch(`${this.baseUrl}${path}`, init);
      const trace = response.headers.get("X-Trace-ID")?.trim() || undefined;
      if (
        response.status === 401 &&
        request.auth !== false &&
        !retried &&
        expectedIdentity !== undefined &&
        (await this.refreshForRequest(expectedIdentity))
      ) {
        if (
          !requestIdentityMatches(
            this.session,
            expectedIdentity,
            this.requestEpoch,
          )
        ) {
          throw new DOMException("请求期间会话身份已变化", "AbortError");
        }
        return this.request<T>(method, path, request, true, expectedIdentity);
      }
      if (!response.ok) {
        const detail = await readBody(response);
        throw new ApiError(
          errorMessage(detail) ?? `请求失败 (${response.status})`,
          response.status,
          detail,
          trace,
        );
      }
      if (response.status === 204) return undefined as T;
      const body = await readBody(response);
      if (isEnvelope(body)) {
        if (body.code !== 0) {
          throw new ApiError(
            body.message || `请求失败 (${response.status})`,
            response.status,
            body,
            trace,
          );
        }
        return body.data as T;
      }
      return body as T;
    } catch (error) {
      // 取消的请求（AbortController / 组件卸载）不算失败：不 toast、不走全局处理，直接抛出。
      if (isAbortError(error)) {
        throw error;
      }
      const config = { method, path };
      let thrownError = error;
      if (request.onError) {
        const result = request.onError(thrownError, config);
        // onError 返回 undefined 视为「已处理」：不再走全局 handler，但仍需抛出，
        // 绝不静默 resolve undefined。返回 Error 则用其替换后交给全局 handler。
        if (result !== undefined) {
          thrownError = result;
          if (this.globalErrorHandler) {
            this.globalErrorHandler(thrownError, config);
          }
        }
        throw thrownError;
      }
      if (this.globalErrorHandler) {
        this.globalErrorHandler(thrownError, config);
      }
      throw thrownError;
    }
  }

  private async requestStream(
    method: string,
    path: string,
    request: ApiRequest = {},
    retried = false,
    expectedIdentity = authenticatedSessionIdentity(
      this.session,
      this.requestEpoch,
      request.auth,
    ),
  ): Promise<Response> {
    if (
      expectedIdentity &&
      (!requestIdentityMatches(
        this.session,
        expectedIdentity,
        this.requestEpoch,
      ) ||
        !browserCookieIdentityMatches(expectedIdentity))
    ) {
      throw new DOMException("请求发起前会话身份已变化", "AbortError");
    }
    const headers = new Headers(request.headers);
    headers.set("Accept", "text/event-stream");
    headers.set("X-Api-Version", "v1");
    if (request.body !== undefined) {
      headers.set("Content-Type", "application/json");
    }
    if (request.auth !== false && this.session?.token.accessToken) {
      headers.set("Authorization", `Bearer ${this.session.token.accessToken}`);
    }
    const init: RequestInit = {
      credentials: "include",
      headers,
      method,
    };
    if (request.body !== undefined) {
      init.body = JSON.stringify(request.body);
    }
    if (request.signal !== undefined) init.signal = request.signal;
    try {
      const response = await fetch(`${this.baseUrl}${path}`, init);
      const trace = response.headers.get("X-Trace-ID")?.trim() || undefined;
      if (
        response.status === 401 &&
        request.auth !== false &&
        !retried &&
        expectedIdentity !== undefined &&
        (await this.refreshForRequest(expectedIdentity))
      ) {
        if (
          !requestIdentityMatches(
            this.session,
            expectedIdentity,
            this.requestEpoch,
          )
        ) {
          throw new DOMException("请求期间会话身份已变化", "AbortError");
        }
        return this.requestStream(
          method,
          path,
          request,
          true,
          expectedIdentity,
        );
      }
      if (!response.ok) {
        const detail = await readBody(response);
        throw new ApiError(
          errorMessage(detail) ?? `请求失败 (${response.status})`,
          response.status,
          detail,
          trace,
        );
      }
      return response;
    } catch (error) {
      if (isAbortError(error)) {
        throw error;
      }
      const config = { method, path };
      let thrownError = error;
      if (request.onError) {
        const result = request.onError(thrownError, config);
        if (result !== undefined) {
          thrownError = result;
          this.globalErrorHandler?.(thrownError, config);
        }
        throw thrownError;
      }
      this.globalErrorHandler?.(thrownError, config);
      throw thrownError;
    }
  }

  private refresh(): Promise<boolean> {
    const generation = this.sessionGeneration;
    if (!this.refreshFlight || this.refreshFlight.generation !== generation) {
      const promise = this.rotate().finally(() => {
        if (this.refreshFlight?.promise === promise) {
          this.refreshFlight = null;
        }
      });
      this.refreshFlight = { generation, promise };
    }
    return this.refreshFlight.promise;
  }

  private async refreshForRequest(expected: SessionIdentity): Promise<boolean> {
    if (!requestIdentityMatches(this.session, expected, this.requestEpoch)) {
      return false;
    }
    if (!browserCookieIdentityMatches(expected)) return false;
    if (this.session?.token.accessToken !== expected.accessToken) {
      return true;
    }
    if (!(await this.refresh())) return false;
    return requestIdentityMatches(this.session, expected, this.requestEpoch);
  }

  private async rotate(): Promise<boolean> {
    const observedAccessToken = this.session?.token.accessToken;
    const observedIdentity = authenticatedSessionIdentity(
      this.session,
      this.requestEpoch,
      true,
    );
    return this.withSessionCookieLock(async () => {
      if (observedIdentity && !browserCookieIdentityMatches(observedIdentity)) {
        return false;
      }
      if (observedAccessToken) {
        // The previous lock holder broadcasts its rotated pair before release.
        await new Promise((resolve) => setTimeout(resolve, 20));
        if (this.session?.token.accessToken !== observedAccessToken) {
          return this.session !== null;
        }
      }
      return this.rotateCurrent();
    });
  }

  private withSessionCookieLock<T>(callback: () => Promise<T>): Promise<T> {
    const execute = () => {
      const locks = globalThis.navigator?.locks;
      return locks ? locks.request(sessionRefreshLock, callback) : callback();
    };
    const result = this.sessionCookieTail.then(execute, execute);
    this.sessionCookieTail = result.then(
      () => undefined,
      () => undefined,
    );
    return result;
  }

  private async rotateCurrent(): Promise<boolean> {
    const current = this.session;
    if (!current) return false;
    const generation = this.sessionGeneration;
    try {
      const token = await this.api.post<SessionRefreshResult>(
        "/user/auth/refresh",
        {
          auth: false,
        },
      );
      if (this.sessionGeneration !== generation) return false;
      const latest = this.session;
      if (!latest) return false;
      this.commit({ ...latest, token: browserSessionToken(token) });
      return true;
    } catch {
      if (this.sessionGeneration === generation) this.commit(null);
      return false;
    }
  }

  private async restoreFromCookie(): Promise<void> {
    try {
      await this.withSessionCookieLock(async () => {
        // Let a previous lock holder's BroadcastChannel update arrive first.
        await new Promise((resolve) => setTimeout(resolve, 20));
        if (!this.session) await this.restoreFromCookieCurrent();
      });
    } finally {
      this.finishSessionRestore();
    }
  }

  private async restoreFromCookieCurrent(): Promise<void> {
    if (this.session) return;
    try {
      const result = await this.api.post<SessionRestoreResult>(
        "/user/auth/session/restore",
        {
          auth: false,
        },
      );
      const tenant = tenantFromResult(result.tenant);
      const restored = {
        identifier: result.user.email,
        tenant,
        tenants: result.tenants.map(tenantFromResult),
        token: {
          accessToken: result.accessToken,
          accessExpiresAt: result.accessExpiresAt,
          refreshExpiresAt: result.refreshExpiresAt,
        },
        user: result.user,
      } satisfies Session;
      allowBrowserRestore();
      writeBrowserCookieIdentity(restored);
      this.commit(restored);
    } catch {
      // A failed background restore is retryable on the next browser startup.
    }
  }

  private commit(next: Session | null, broadcast = true): void {
    if (sessionCredentialChanged(this.session, next)) {
      this.sessionGeneration += 1;
    }
    if (sessionBoundaryChanged(this.session, next)) this.requestEpoch += 1;
    this.session = next ? browserSession(next) : null;
    this.finishSessionRestore(false);
    if (this.session) {
      this.storage?.setItem(storageKey, JSON.stringify(this.session));
    } else {
      this.storage?.removeItem(storageKey);
    }
    if (broadcast && this.syncChannel) {
      this.broadcastSession();
    }
    this.notifications.sessionChanged();
    this.listeners.forEach((listener) => listener());
  }

  private broadcastSession(): void {
    if (!this.syncChannel) return;
    const at = Math.max(Date.now(), this.syncTimestamp + 1);
    this.syncTimestamp = at;
    this.syncTimestampSource = this.syncSource;
    const cookieVersion = readBrowserCookieIdentity()?.version;
    this.syncChannel.postMessage({
      at,
      ...(cookieVersion === undefined ? {} : { cookieVersion }),
      session: this.session,
      source: this.syncSource,
      type: "session",
    } satisfies SessionUpdateMessage);
  }

  private finishSessionRestore(notify = true): void {
    if (this.readyTimer) {
      clearTimeout(this.readyTimer);
      this.readyTimer = null;
    }
    if (this.ready) return;
    this.ready = true;
    if (notify) this.listeners.forEach((listener) => listener());
  }

  private restore(): Session | null {
    const raw = this.storage?.getItem(storageKey);
    if (!raw) return null;
    try {
      const value: unknown = JSON.parse(raw);
      if (!restorableSession(value)) {
        throw new Error("invalid session");
      }
      const session = browserSession(value);
      this.storage?.setItem(storageKey, JSON.stringify(session));
      return session;
    } catch {
      this.storage?.removeItem(storageKey);
      return null;
    }
  }
}

function browserSessionStorage(storage: Storage | null): boolean {
  if (!storage || typeof window === "undefined") return false;
  try {
    return storage === window.sessionStorage;
  } catch {
    return false;
  }
}

function browserRestoreSuppressed(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem(sessionRestoreSuppressedKey) === "1";
  } catch {
    return false;
  }
}

function suppressBrowserRestore(): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(sessionRestoreSuppressedKey, "1");
  } catch {
    // A local logout still clears the in-memory session when storage is unavailable.
  }
}

function allowBrowserRestore(): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(sessionRestoreSuppressedKey);
  } catch {
    // The HttpOnly cookie remains authoritative when storage is unavailable.
  }
}

function browserCookieIdentityMatches(expected: SessionIdentity): boolean {
  if (typeof window === "undefined") return true;
  if (!("localStorage" in window)) return true;
  try {
    const value = readBrowserCookieIdentity();
    if (!value) return expected.cookieVersion === undefined;
    return (
      value.tenantId === expected.tenantId &&
      value.userId === expected.userId &&
      value.version === expected.cookieVersion
    );
  } catch {
    return false;
  }
}

function browserCookieVersionFor(session: Session): { cookieVersion?: number } {
  const value = readBrowserCookieIdentity();
  if (
    value?.tenantId !== session.tenant.id ||
    value.userId !== session.user.id
  ) {
    return {};
  }
  return { cookieVersion: value.version };
}

function readBrowserCookieIdentity(): BrowserCookieIdentity | undefined {
  if (typeof window === "undefined" || !("localStorage" in window)) return;
  try {
    return parseBrowserCookieIdentity(
      window.localStorage.getItem(sessionCookieIdentityKey),
    );
  } catch {
    return;
  }
}

function parseBrowserCookieIdentity(
  raw: string | null,
): BrowserCookieIdentity | undefined {
  if (!raw) return;
  try {
    const value: unknown = JSON.parse(raw);
    if (
      typeof value !== "object" ||
      value === null ||
      !("tenantId" in value) ||
      !(typeof value.tenantId === "string" || value.tenantId === null) ||
      !("userId" in value) ||
      !(typeof value.userId === "string" || value.userId === null) ||
      !("version" in value) ||
      typeof value.version !== "number"
    ) {
      return;
    }
    return value as BrowserCookieIdentity;
  } catch {
    return;
  }
}

function writeBrowserCookieIdentity(session: Session | null): void {
  if (typeof window === "undefined") return;
  if (!("localStorage" in window)) return;
  try {
    const previous = readBrowserCookieIdentity();
    window.localStorage.setItem(
      sessionCookieIdentityKey,
      JSON.stringify({
        tenantId: session?.tenant.id ?? null,
        userId: session?.user.id ?? null,
        version: (previous?.version ?? 0) + 1,
      } satisfies BrowserCookieIdentity),
    );
  } catch {
    // Instance-local generation checks remain active when shared storage is unavailable.
  }
}

function tenantFromResult(result: TenantOptionResult): Tenant {
  return {
    id: result.tenantId,
    name: result.tenantName || tenantLabel(result.tenantId),
    stepUpRequired: result.stepUpRequired,
    ...(result.tenantKind ? { kind: result.tenantKind } : {}),
  };
}

function sessionSyncMessage(value: unknown): SessionSyncMessage | null {
  if (
    typeof value !== "object" ||
    value === null ||
    !("type" in value) ||
    !("source" in value) ||
    typeof value.source !== "string"
  ) {
    return null;
  }
  if (value.type === "session-request") {
    return value as SessionRequestMessage;
  }
  if (
    value.type !== "session" ||
    !("at" in value) ||
    typeof value.at !== "number" ||
    !("session" in value) ||
    (value.session !== null && !restorableSession(value.session))
  ) {
    return null;
  }
  return value as SessionSyncMessage;
}

function restorableSession(value: unknown): value is Session {
  if (typeof value !== "object" || value === null) return false;
  const session = value as Partial<Session>;
  return Boolean(
    session.token?.accessToken &&
    session.token.accessExpiresAt &&
    session.user?.id &&
    session.tenant?.id &&
    Date.parse(session.token.refreshExpiresAt) > Date.now(),
  );
}

function browserSession(session: Session): Session {
  return {
    ...session,
    token: browserSessionToken(session.token),
  };
}

function sessionCredentialChanged(
  current: Session | null,
  next: Session | null,
): boolean {
  if (!current || !next) return current !== next;
  return (
    current.tenant.id !== next.tenant.id ||
    current.user.id !== next.user.id ||
    current.token.accessToken !== next.token.accessToken ||
    current.token.refreshExpiresAt !== next.token.refreshExpiresAt
  );
}

function sessionBoundaryChanged(
  current: Session | null,
  next: Session | null,
): boolean {
  if (!current || !next) return current !== next;
  return (
    current.tenant.id !== next.tenant.id ||
    current.user.id !== next.user.id ||
    current.token.refreshExpiresAt !== next.token.refreshExpiresAt
  );
}

interface SessionIdentity {
  accessToken: string;
  cookieVersion?: number;
  generation: number;
  tenantId: string;
  userId: string;
}

function authenticatedSessionIdentity(
  session: Session | null,
  generation: number,
  auth: boolean | undefined,
): SessionIdentity | undefined {
  if (auth === false || !session) return undefined;
  return {
    accessToken: session.token.accessToken,
    ...browserCookieVersionFor(session),
    generation,
    tenantId: session.tenant.id,
    userId: session.user.id,
  };
}

function requestIdentityMatches(
  session: Session | null,
  expected: SessionIdentity,
  generation: number,
): boolean {
  return (
    generation === expected.generation &&
    session?.tenant.id === expected.tenantId &&
    session.user.id === expected.userId
  );
}

function browserSessionToken(
  token: Pick<
    TokenPair,
    "accessToken" | "accessExpiresAt" | "refreshExpiresAt"
  >,
): Session["token"] {
  return {
    accessToken: token.accessToken,
    accessExpiresAt: token.accessExpiresAt,
    refreshExpiresAt: token.refreshExpiresAt,
  };
}

function tenantLabel(id: string): string {
  return `组织 ${id.slice(0, 8).toUpperCase()}`;
}

function hexBytes(value: Uint8Array): string {
  let result = "";
  for (const byte of value) result += byte.toString(16).padStart(2, "0");
  return result;
}

function staticSiteArchiveContentType(file: File): string {
  const contentType = file.type.trim().toLowerCase();
  if (
    contentType === "application/zip" ||
    contentType === "application/x-zip-compressed" ||
    contentType === "application/gzip" ||
    contentType === "application/x-gzip"
  ) {
    return contentType;
  }
  const name = file.name.toLowerCase();
  if (name.endsWith(".zip")) return "application/zip";
  if (name.endsWith(".tar.gz") || name.endsWith(".tgz")) {
    return "application/gzip";
  }
  return "application/octet-stream";
}

// isAbortError 判定错误是否为请求取消（fetch abort）。取消不应触发错误提示。
function isAbortError(error: unknown): boolean {
  return (
    (typeof DOMException !== "undefined" &&
      error instanceof DOMException &&
      error.name === "AbortError") ||
    (error instanceof Error && error.name === "AbortError")
  );
}

// pageQuery 拼 keyset 分页查询串；pageToken 为空时省略（首页）。
function pageQuery(pageToken: string, pageSize: number, keyword = ""): string {
  const params = new URLSearchParams();
  if (pageToken) params.set("pageToken", pageToken);
  params.set("pageSize", String(pageSize));
  if (keyword.trim()) params.set("keyword", keyword.trim());
  return `?${params.toString()}`;
}

function offsetPageQuery(page: number, pageSize: number, keyword = ""): string {
  const params = new URLSearchParams({
    page: String(page),
    pageSize: String(pageSize),
  });
  if (keyword.trim()) params.set("keyword", keyword.trim());
  return `?${params.toString()}`;
}

async function readBody(response: Response): Promise<unknown> {
  const text = await response.text();
  if (!text) return undefined;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return text;
  }
}

function errorMessage(detail: unknown): string | undefined {
  if (!detail || typeof detail !== "object") return undefined;
  const record = detail as Record<string, unknown>;
  if (typeof record.message === "string") return record.message;
  if (typeof record.detail === "string") return record.detail;
  if (typeof record.title === "string") return record.title;
  if (typeof record.error === "string") {
    const description =
      typeof record.error_description === "string"
        ? record.error_description
        : undefined;
    return oauthErrorMessage(record.error, description);
  }
  return undefined;
}

function oauthErrorMessage(code: string, description?: string): string {
  const messages: Record<string, string> = {
    access_denied: "当前账号或租户无权批准此 OAuth 请求",
    invalid_client: "OAuth 客户端不存在、已停用或认证失败",
    invalid_grant: "授权凭据已过期、已使用，或回调地址与 PKCE 校验失败",
    invalid_request: "OAuth 请求参数不完整或格式无效",
    invalid_scope: "请求的 OAuth 权限不存在，或尚未授权给该应用",
    invalid_token: "OAuth 访问令牌无效、已过期或已撤销",
    temporarily_unavailable: "OAuth 服务暂时不可用，请稍后重试",
    unauthorized_client: "OAuth 客户端未获准使用当前授权方式",
    unsupported_grant_type: "OAuth 客户端不支持当前授权方式",
  };
  return messages[code] ?? description ?? `OAuth 请求失败（${code}）`;
}

// isEnvelope 判定响应体是否为统一信封 {code, message, ...}。
function isEnvelope(body: unknown): body is Envelope {
  if (!body || typeof body !== "object") return false;
  const record = body as Record<string, unknown>;
  return typeof record.code === "number" && typeof record.message === "string";
}

function envelopeMeta(detail: unknown): Envelope["meta"] | undefined {
  return isEnvelope(detail) ? detail.meta : undefined;
}

function envelopeCode(detail: unknown): number | undefined {
  return isEnvelope(detail) ? detail.code : undefined;
}
